import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Video, CalendarIcon, CheckCircle2 } from "lucide-react";

import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { getVisitorId } from "@/lib/visitorId";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  validatePhoneNumber,
  formatPhoneInput,
  phoneValidationMessages,
} from "@/lib/phoneValidation";


export const VideoCallBooking = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const phoneMessages = phoneValidationMessages[language];
  const [isOpen, setIsOpen] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [teamsLink, setTeamsLink] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState<Date>();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [bookedSlots, setBookedSlots] = useState<{date: string, time: string}[]>([]);
  const [phoneError, setPhoneError] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    time: "",
    message: "",
    consultant: "ad",
  });



  const allTimeSlots = [
    "07:00", "07:30", "08:00", "08:30", "09:00", "09:30",
    "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
    "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
    "16:00", "16:30", "17:00", "17:30", "18:00", "18:30",
    "19:00", "19:30"
  ];

  const inRange = (time: string, from: number, to: number) => {
    const [hour, minute] = time.split(":").map(Number);
    const value = hour + minute / 60;
    return value >= from && value <= to;
  };

  // Fetch booked appointments when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchBookedSlots();
    }
  }, [isOpen]);

  const fetchBookedSlots = async () => {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('preferred_date, preferred_time');

      if (error) throw error;

      const slots = (data || []).map(apt => ({
        date: new Date(apt.preferred_date).toDateString(),
        time: apt.preferred_time
      }));
      
      setBookedSlots(slots);
    } catch (error) {
      console.error('Error fetching booked slots:', error);
    }
  };

  // Filter time slots based on consultant, selected day and booked appointments
  const getAvailableTimeSlots = () => {
    if (!date) return [];

    const dayOfWeek = date.getDay();
    const dateString = date.toDateString();
    const isAD = formData.consultant === "ad";

    let daySlots: string[] = [];

    if (isAD) {
      // Außendienst: Mo–Do 07:00–20:00, Fr 07:00–18:00
      if (dayOfWeek >= 1 && dayOfWeek <= 4) {
        daySlots = allTimeSlots.filter(time => inRange(time, 7, 19.5));
      } else if (dayOfWeek === 5) {
        daySlots = allTimeSlots.filter(time => inRange(time, 7, 17.5));
      }
    } else {
      // Dr. Kern: Mittwoch 14:00–19:00, Freitag 12:00–19:00, Samstag 10:00–14:00
      if (dayOfWeek === 3) {
        daySlots = allTimeSlots.filter(time => inRange(time, 14, 19));
      } else if (dayOfWeek === 5) {
        daySlots = allTimeSlots.filter(time => inRange(time, 12, 19));
      } else if (dayOfWeek === 6) {
        daySlots = allTimeSlots.filter(time => inRange(time, 10, 14));
      }
    }

    // Filter out already booked slots for this date
    return daySlots.filter(time => {
      const isBooked = bookedSlots.some(
        slot => slot.date === dateString && slot.time === time
      );
      return !isBooked;
    });
  };

  const timeSlots = getAvailableTimeSlots();

  // returns true when the date should be DISABLED
  const isDateAvailable = (checkDate: Date) => {
    if (checkDate < new Date(new Date().setHours(0, 0, 0, 0))) {
      return true;
    }

    const day = checkDate.getDay();

    if (formData.consultant === "ad") {
      // Montag bis Freitag
      return day === 0 || day === 6;
    }

    // Dr. Kern: nur Mittwoch, Freitag, Samstag
    return day !== 3 && day !== 5 && day !== 6;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!date || !formData.time) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie ein Datum und eine Uhrzeit.",
        variant: "destructive",
      });
      return;
    }

    const phoneCheck = validatePhoneNumber(formData.phone);
    if (!phoneCheck.valid) {
      const message = phoneCheck.error
        ? phoneMessages[phoneCheck.error]
        : phoneMessages.invalid;
      setPhoneError(message);
      toast({
        title: "Fehler",
        description: message,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);


    try {
      // Check if slot is still available (double-check before inserting)
      const dateString = date.toDateString();
      const isSlotTaken = bookedSlots.some(
        slot => slot.date === dateString && slot.time === formData.time
      );

      if (isSlotTaken) {
        toast({
          title: "Termin nicht mehr verfügbar",
          description: "Dieser Zeitslot wurde gerade von jemand anderem gebucht. Bitte wählen Sie eine andere Zeit.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        await fetchBookedSlots(); // Refresh available slots
        return;
      }

      const consultantLabel =
        formData.consultant === "kern"
          ? "Videocall mit Dr. Kern"
          : "Videocall mit dem Außendienst (AD)";

      const { data: insertData, error } = await supabase.from('appointments').insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        preferred_date: date.toISOString(),
        preferred_time: formData.time,
        message: formData.message,
        consultant: formData.consultant,
        visitor_id: getVisitorId(),
      }).select().single();

      if (error) throw error;

      const teamsLinkUrl = "https://teams.microsoft.com/l/meetup-join/19%3ameeting_NWUwZTBhZjMtY2FhNC00NTU1LWExY2YtMThlOTYzZThlMTg0%40thread.v2/0?context=%7b%22Tid%22%3a%224897b68e-7063-4af5-bbf8-a6d86752e545%22%2c%22Oid%22%3a%2202ffc0a8-ede5-4eff-a9b2-5daafa8dda73%22%7d";
      setTeamsLink(teamsLinkUrl);

      // Send confirmation email with appointment ID
      try {
        await supabase.functions.invoke('send-appointment-confirmation', {
          body: {
            name: formData.name,
            email: formData.email,
            date: format(date, "EEEE, dd. MMMM yyyy", { locale: de }),
            time: formData.time,
            teamsLink: teamsLinkUrl,
            appointmentId: insertData.id,
            consultant: formData.consultant,
            consultantLabel,
          },
        });

        console.log('Confirmation email sent successfully');
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Don't fail the booking if email fails
      }

      setIsBooked(true);
      
      toast({
        title: "Terminanfrage gesendet!",
        description: "Ihre Anfrage wurde versendet. Sie erhalten eine E-Mail, sobald der Termin bestätigt wurde.",
      });
    } catch (error: any) {
      console.error('Error booking appointment:', error);
      toast({
        title: "Fehler bei der Buchung",
        description: "Bitte versuchen Sie es später erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", time: "", message: "", consultant: "ad" });
    setDate(undefined);
    setIsBooked(false);
    setTeamsLink("");
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button 
          variant="outline"
          size="sm"
          className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
        >
          Termin vereinbaren
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90dvh] overflow-y-auto overscroll-contain">
        {!isBooked ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="w-5 h-5 text-primary" />
                Videocall-Termin vereinbaren
              </DialogTitle>
              <DialogDescription>
                Wählen Sie Ihren Wunschtermin für eine persönliche Beratung per Microsoft Teams.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="booking-name">Name *</Label>
                <Input
                  id="booking-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-2"
                  placeholder="Ihr Name"
                />
              </div>

              <div>
                <Label htmlFor="booking-email">E-Mail *</Label>
                <Input
                  id="booking-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-2"
                  placeholder="ihre@email.de"
                />
              </div>

              <div>
                <Label htmlFor="booking-phone">Telefon *</Label>
                <Input
                  id="booking-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const cleaned = raw.replace(/[^+\d\s()-]/g, "");
                    setFormData({ ...formData, phone: cleaned });
                    if (phoneError) setPhoneError("");
                  }}
                  onBlur={(e) => {
                    const formatted = formatPhoneInput(e.target.value);
                    setFormData((prev) => ({ ...prev, phone: formatted }));
                    const check = validatePhoneNumber(formatted);
                    if (!check.valid) {
                      setPhoneError(
                        check.error ? phoneMessages[check.error] : phoneMessages.invalid
                      );
                    } else {
                      setPhoneError("");
                    }
                  }}
                  required
                  className={cn("mt-2", phoneError && "border-destructive focus-visible:ring-destructive")}
                  placeholder={phoneMessages.placeholder}
                  aria-invalid={!!phoneError}
                  aria-describedby={phoneError ? "booking-phone-error" : undefined}
                />
                {phoneError && (
                  <p id="booking-phone-error" className="text-sm text-destructive mt-1">
                    {phoneError}
                  </p>
                )}
              </div>


              <div>
                <Label>Gesprächspartner *</Label>
                <RadioGroup
                  value={formData.consultant}
                  onValueChange={(value) => {
                    setFormData({ ...formData, consultant: value, time: "" });
                    setDate(undefined);
                  }}
                  className="mt-2 space-y-2"
                >
                  <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                    <RadioGroupItem value="ad" id="consultant-ad" className="mt-0.5" />
                    <Label htmlFor="consultant-ad" className="font-normal cursor-pointer leading-snug">
                      Videocall mit dem Außendienst (AD)
                      <span className="block text-xs text-muted-foreground">
                        Produkte, Handhabung, Bestellung & Preise · Mo - Do 07:00 - 20:00, Fr 07:00 - 18:00
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        Termin wird vom Außendienst bestätigt
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                    <RadioGroupItem value="kern" id="consultant-kern" className="mt-0.5" />
                    <Label htmlFor="consultant-kern" className="font-normal cursor-pointer leading-snug">
                      Videocall mit Dr. Kern
                      <span className="block text-xs text-muted-foreground">
                        Wissenschaftliche & klinische Fragen · Mi 14:00 - 19:00, Fr 12:00 - 19:00, Sa 10:00 - 14:00
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        Termin wird von Dr. Kern bestätigt
                      </span>
                    </Label>
                  </div>
                </RadioGroup>

              </div>


              <div>
                <Label>Wunschdatum *</Label>
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-2",
                        !date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP", { locale: de }) : "Datum wählen"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(newDate) => {
                        setDate(newDate);
                        // Reset time selection when date changes
                        setFormData({ ...formData, time: "" });
                        // Close the calendar popup
                        setIsCalendarOpen(false);
                      }}
                      disabled={isDateAvailable}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="booking-time">Wunschuhrzeit *</Label>
                <Select 
                  value={formData.time} 
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                  disabled={!date}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={date ? "Uhrzeit wählen" : "Bitte zuerst Datum wählen"} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.length > 0 ? (
                      timeSlots.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time} Uhr
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        Keine Zeiten verfügbar
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {date && timeSlots.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.consultant === "ad"
                      ? date.getDay() === 5
                        ? "Außendienst Freitag: Termine von 07:00 - 18:00 Uhr"
                        : "Außendienst Mo - Do: Termine von 07:00 - 20:00 Uhr"
                      : date.getDay() === 3
                        ? "Dr. Kern Mittwoch: Termine von 14:00 - 19:00 Uhr"
                        : date.getDay() === 5
                          ? "Dr. Kern Freitag: Termine von 12:00 - 19:00 Uhr"
                          : "Dr. Kern Samstag: Termine von 10:00 - 14:00 Uhr"}
                  </p>
                )}

                {date && timeSlots.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                    ⚠️ Alle Zeitslots für diesen Tag sind bereits vergeben. Bitte wählen Sie ein anderes Datum.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="booking-message">Nachricht (optional)</Label>
                <Textarea
                  id="booking-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mt-2 min-h-[80px]"
                  placeholder="Teilen Sie uns mit, worum es in der Beratung gehen soll..."
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Wird gesendet..." : "Termin anfragen"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-2xl mb-2">Terminanfrage gesendet!</DialogTitle>
              <DialogDescription className="text-base">
                Wir haben Ihre Anfrage erhalten und werden Sie per E-Mail benachrichtigen, sobald der Termin bestätigt wurde.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                <strong>Wichtiger Hinweis:</strong>
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Ihr Termin muss noch von uns bestätigt werden. Sie erhalten in Kürze eine E-Mail mit der finalen Bestätigung und dem Teams-Link.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={resetForm}
              className="w-full mt-4"
            >
              Schließen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};